// app/javascript/controllers/hero_onboarding_controller.js
import { Controller } from "@hotwired/stimulus"
import Cropper from "cropperjs"
import * as bootstrap from "bootstrap"

export default class extends Controller {
    static targets = [
        "childNameInput",
        "childNameDisplay",
        "previewContainer",
        "imagePreview",
        "imagePlaceholder",
        "removeImageButton",
        "cropImage",
        "cropError",
        "croppedImageField", "previewButton", "submitButton", "formError"
    ]

    connect() {
        this.cropper = null
        this.cropModal = new bootstrap.Modal(document.getElementById("heroCropModal"))
        this.previewModal = new bootstrap.Modal(document.getElementById("heroPreviewModal"))
        this.originalFileInput = document.getElementById("become_hero_upload_inp")
        this.previewButtonTarget.classList.remove("d-none")
        this.syncChildName()
        this.renderPhoto()
        this.cropHidden = () => {
            this.destroyCropper()
            this.originalFileInput.value = ""
            this.cropping = false
        }
        document.getElementById("heroCropModal").addEventListener("hidden.bs.modal", this.cropHidden)
        document.getElementById("hero-form-errors")?.focus()
    }

    disconnect() {
        this.destroyCropper()
        document.getElementById("heroCropModal")?.removeEventListener("hidden.bs.modal", this.cropHidden)
        ;[[this.cropModal, "heroCropModal"], [this.previewModal, "heroPreviewModal"]].forEach(([modal, id]) => {
            if (!modal) return
            document.getElementById(id)?.classList.remove("fade")
            modal.hide()
            modal.dispose()
        })
    }

    syncChildName() {
        const value = this.childNameInputTarget.value.trim()
        this.childNameDisplayTargets.forEach((el) => {
            el.textContent = value.length > 0 ? value : "your child"
        })
    }

    buildPreview() {
        const form = this.element.querySelector("#become_hero_form")
        if (!this.canSubmit() || !form.reportValidity()) return
        const formData = new FormData(form)
        const childName = (formData.get("child_name") || "your child").toString()

        this.previewContainerTarget.innerHTML = `
      <div class="mb-3"><strong>First name:</strong> ${this.escape(formData.get("first_name"))}</div>
      <div class="mb-3"><strong>Last name:</strong> ${this.escape(formData.get("last_name"))}</div>
      <div class="mb-3"><strong>Email:</strong> ${this.escape(formData.get("email"))}</div>
      <div class="mb-3"><strong>Child name:</strong> ${this.escape(childName)}</div>
      <div class="mb-3"><strong>Instagram:</strong> ${this.escape(formData.get("parent_instagram"))}</div>
      <div class="mb-3"><strong>Facebook:</strong> ${this.escape(formData.get("parent_facebook"))}</div>
      <hr>
      <div class="mb-3">
        <strong>Why did ${this.escape(childName)} choose this charity?</strong>
        <p class="mt-2 mb-0 hero-preview-answer">${this.escape(formData.get("answer"))}</p>
      </div>
      <div class="mb-3">
        <strong>What gifts did ${this.escape(childName)} choose?</strong>
        <p class="mt-2 mb-0 hero-preview-answer">${this.escape(formData.get("gifts_chosen"))}</p>
      </div>
      <div class="mb-3">
        <strong>Why did ${this.escape(childName)} choose the birthday present?</strong>
        <p class="mt-2 mb-0 hero-preview-answer">${this.escape(formData.get("answer2"))}</p>
      </div>
      ${this.validPhotoData(this.croppedImageFieldTarget.value) ? `
        <hr>
        <div>
          <strong>Photo preview:</strong>
          <div class="mt-3">
            <img src="${this.escape(this.croppedImageFieldTarget.value)}" class="img-fluid rounded-3" alt="Hero preview image">
          </div>
        </div>
      ` : ""}
      ${formData.get("video_upload_url") ? `<p>Video saved: ${this.escape(formData.get("video_name"))}</p>` : ""}
    `
        this.previewModal.show()
    }

    videoState(event) { this.uploadState = event.detail.state }

    canSubmit() {
        if (this.cropping || ["uploading", "saving", "error"].includes(this.uploadState)) {
            this.formErrorTarget.textContent = "Please finish your photo/video, retry saving it, or remove it before continuing."
            this.formErrorTarget.classList.remove("d-none")
            this.formErrorTarget.scrollIntoView({ block: "center" })
            return false
        }
        this.formErrorTarget.classList.add("d-none")
        return true
    }

    beforeSubmit(event) {
        if (this.submitting || !this.canSubmit()) { event.preventDefault(); return }
        this.submitting = true
        this.submitButtonTargets.forEach(button => { button.disabled = true; button.textContent = "Saving your story…" })
    }

    openCropper(event) {
        const file = event.target.files?.[0]
        if (!file) return
        if (file.size > 10 * 1024 * 1024 || !["image/jpeg", "image/png", "image/gif"].includes(file.type)) {
            event.target.value = ""
            this.formErrorTarget.textContent = "Please choose a JPG, PNG or GIF photo no larger than 10 MB."
            this.formErrorTarget.classList.remove("d-none")
            return
        }

        this.hideCropError()

        const reader = new FileReader()
        this.cropping = true
        reader.onerror = () => {
            this.cropping = false
            this.originalFileInput.value = ""
            this.formErrorTarget.textContent = "We couldn't read that photo. Please choose it again."
            this.formErrorTarget.classList.remove("d-none")
        }
        reader.onload = async (e) => {
            if (!this.element.isConnected) return
            this.cropImageTarget.src = e.target.result
            const shown = new Promise(resolve => document.getElementById("heroCropModal").addEventListener("shown.bs.modal", resolve, { once: true }))
            this.cropModal.show()
            await shown
            try {
                this.destroyCropper()
                this.cropper = new Cropper(this.cropImageTarget)
                const image = this.cropper.getCropperImage()
                image.rotatable = true
                const selection = this.cropper.getCropperSelection()
                selection.aspectRatio = 2
                selection.initialAspectRatio = 2
                selection.initialCoverage = 0.8
                selection.keyboard = true
                await image.$ready()
                image.$center("contain")
                selection.$reset().$center()
            } catch (_) { this.showCropError() }
        }

        reader.readAsDataURL(file)
    }

    rotateLeft() {
        this.cropper?.getCropperImage()?.$rotate("-30deg")
    }

    rotateRight() {
        this.cropper?.getCropperImage()?.$rotate("30deg")
    }

    resetCrop() {
        this.cropper?.getCropperImage()?.$resetTransform().$center("contain")
        this.cropper?.getCropperSelection()?.$reset().$center()
    }

    discardCrop() {
        this.destroyCropper()
        this.cropModal.hide()
        if (this.originalFileInput) this.originalFileInput.value = ""
    }

    async saveCrop() {
        if (!this.cropper) return
        const cropper = this.cropper

        try {
            const canvas = await this.cropper.getCropperSelection().$toCanvas({
                width: 2160,
                height: 1080,
                beforeDraw(context, output) {
                    context.fillStyle = "#fff"
                    context.fillRect(0, 0, output.width, output.height)
                }
            })

            if (this.cropper !== cropper || !this.element.isConnected) return
            if (!canvas) {
                this.showCropError()
                return
            }

            const dataUrl = canvas.toDataURL("image/jpeg", 0.92)

            this.croppedImageFieldTarget.value = dataUrl
            this.renderPhoto()
            this.cropping = false
            this.cropModal.hide()
        } catch (error) {
            console.error(error)
            this.showCropError()
        }
    }

    removeImage() {
        this.croppedImageFieldTarget.value = ""
        this.renderPhoto()
        if (this.originalFileInput) this.originalFileInput.value = ""
    }

    destroyCropper() {
        if (this.cropper) {
            this.cropper.getCropperCanvas()?.remove()
            this.cropper = null
        }
    }

    renderPhoto() {
        const value = this.croppedImageFieldTarget.value
        const present = this.validPhotoData(value)
        this.imagePreviewTarget.src = present ? value : ""
        this.imagePreviewTarget.classList.toggle("d-none", !present)
        this.imagePlaceholderTarget.classList.toggle("d-none", present)
        this.removeImageButtonTarget.classList.toggle("d-none", !present)
    }

    showCropError() {
        this.cropErrorTarget.classList.remove("d-none")
    }

    hideCropError() {
        this.cropErrorTarget.classList.add("d-none")
    }

    validPhotoData(value) {
        return /^data:image\/(jpeg|png|gif);base64,[A-Za-z0-9+/=\r\n]+$/.test(value)
    }

    escape(value) {
        const div = document.createElement("div")
        div.textContent = value || ""
        return div.innerHTML
    }
}
